/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import List from "@atg/icf-react-components/components/List";
import Popover from "@atg/icf-react-components/components/Popover";
import ThemeProvider from "@atg/icf-react-components/ThemeProvider";
import ITheme, { PlatformId, ThemeMode } from "@atg/icf-react-components/themes/Theme";
import { FC, useRef, useState } from "react";
import { createUseStyles } from "react-jss";

import { JSS_MEDIA_DESKTOP_MIN, JSS_MEDIA_MOBILE_MAX, JSS_MEDIA_TABLET_MAX, JSS_MEDIA_TABLET_MIN } from "~/constants";
import NavItemText from "~/features/header/components/NavItemText";
import useOnClickOutside from "~/hooks/useOnClickOutside";
import useOnLanding from "~/hooks/useOnLanding";
import { ComponentBaseProps } from "~/types";

interface StyleProps {
  onLanding: boolean;
}

const useStyles = createUseStyles<string, StyleProps, ITheme>((theme) => ({
  button: {
    display: "flex",
    alignItems: "center",
    lineHeight: 1.45,
    "&:hover $arrowIcon": {
      "&::after, &::before": {
        backgroundColor: theme.colors.primary,
      },
    },
  },
  arrowIcon: {
    display: "block",
    position: "relative",
    width: "7px",
    height: "10px",
    // eslint-disable-next-line @atg/css-rtl-support
    marginLeft: "7px",
    marginBottom: "2px",
    "&::after, &::before": {
      content: `""`,
      position: "absolute",
      width: "7px",
      height: "1px",
      backgroundColor: theme.colors.baseContrast100,
      transition: "background-color 0.3s ease-in-out",
      bottom: 0,
      transformOrigin: "bottom center",
    },
    "&::after": {
      // eslint-disable-next-line @atg/css-rtl-support
      transform: "rotate(50deg) translateX(-50%)",
      // eslint-disable-next-line @atg/css-rtl-support
      left: 0,
    },
    "&::before": {
      // eslint-disable-next-line @atg/css-rtl-support
      transform: "rotate(-50deg) translateX(50%)",
      // eslint-disable-next-line @atg/css-rtl-support
      right: 0,
    },
  },
  mobileList: {
    [JSS_MEDIA_TABLET_MIN]: {
      display: (props) => (props.onLanding ? "" : "none"),
    },
    [JSS_MEDIA_DESKTOP_MIN]: {
      display: "none",
    },
  },
  popover: {
    paddingTop: "10px",
    [JSS_MEDIA_MOBILE_MAX]: {
      display: "none",
    },
    [JSS_MEDIA_TABLET_MAX]: {
      display: (props) => (props.onLanding ? "none" : ""),
    },
  },
  listWrapper: {
    width: "100%",
    height: "100%",
  },
  popoverContent: {
    padding: 0,
  },
}));

interface Props extends ComponentBaseProps {
  title: string;
  navItemId: string;
}

const MenuItemWithSubmenu: FC<Props> = ({ title, navItemId, children }) => {
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const onLanding = useOnLanding();

  const classes = useStyles({ onLanding });

  const [isOpen, setIsOpen] = useState(false);

  useOnClickOutside(listWrapperRef, () => setIsOpen(false), isOpen);

  return (
    <div>
      <NavItemText>
        <button onClick={() => setIsOpen((prev) => !prev)} id={navItemId} className={classes.button}>
          {title} <span className={classes.arrowIcon} />
        </button>
      </NavItemText>

      {isOpen && (
        <div className={classes.mobileList}>
          <List>{children}</List>
        </div>
      )}
      <ThemeProvider platformId={PlatformId.cambuilder} themeMode={ThemeMode.light}>
        <Popover
          targetId={navItemId}
          disableOpenOnHover
          closeOnClick
          open={isOpen}
          onClick={() => setIsOpen(false)}
          overrides={{ root: classes.popover, content: classes.popoverContent }}
        >
          <div className={classes.listWrapper} ref={listWrapperRef}>
            <List>{children}</List>
          </div>
        </Popover>
      </ThemeProvider>
    </div>
  );
};

export default MenuItemWithSubmenu;
